<template>
  <div>
    <Modal
      :is-open="unblockModalOpen"
      :title="getString('dialog_unblock_title')"
      @close="closeUnblockModal"
    >
      <BaseText size="md" data-testid="confirmation-text">
        {{ getString('dialog_unblock_text') }}
      </BaseText>
      <template #buttons>
        <ModalButton @click="closeUnblockModal">Não</ModalButton>
        <ModalButton @click="unblockAccount">Sim</ModalButton>
      </template>
    </Modal>
    <LoaderIndicator v-if="isLoading" class="background" with-container />
    <div v-else class="background">
      <NavBar transparent class="nav">
        <template #center>
          <BaseText size="p5" :color="NebraskaColors.elementPrimary">
            Redefinir Senha
          </BaseText>
        </template>
      </NavBar>
      <div class="content">
        <Heading class="text-h-1 title" :level="2">Defina a nova senha</Heading>
        <BaseText
          class="subtitle"
          size="p4"
          :color="NebraskaColors.textSecondary"
        >
          A senha deve ter pelo menos 8 caracteres. Evite usar senhas comuns
          como datas e sequências.
        </BaseText>
        <div class="form">
          <InputPassword
            v-model="password"
            class="mb"
            label="Nova senha"
            :password-strength="passwordScore"
            :password-strength-color="passwordStrengthColor"
            :helper-text="passwordMessage"
            @input="onInputChange"
          />
          <InputPassword
            v-model="passwordConfirm"
            class="mb"
            label="Confirme a nova senha"
          />
        </div>
      </div>
      <ButtonContainer>
        <template #button-primary>
          <ButtonPrimary
            data-testid="submit-button"
            :disabled="!enableButton"
            :loading="comittingSetup"
            label="Criar Senha"
            size="large"
            @click="() => commitSetup()"
          />
        </template>
      </ButtonContainer>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';
import { debounce } from 'lodash-es';
import { getString } from '@/modules/common/helpers/resources';
import {
  checkResetToken,
  commitResetToken,
  getThemeByParam,
  unblockHash
} from '@/modules/common/services/http/account';
import { NotificationTypes } from '@/modules/common/services/http/notification';
import { verifyPassword } from '@/modules/authentication/helpers';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { notify } from '@/modules/common/helpers/notification';
import {
  BaseText,
  ButtonContainer,
  ButtonPrimary,
  Heading,
  InputPassword,
  LoaderIndicator,
  Modal,
  NavBar
} from '@warrenbrasil/nebraska-web';
import { getDomainAndPlatform } from '@/modules/common/helpers/onboarding/getDomainAndPlatform';

const route = useRoute();
const router = useRouter();

const isLoading = ref(true);
const comittingSetup = ref(false);
const setupToken = ref(route.params.token as string);
const password = ref('');
const passwordConfirm = ref('');
const passwordScore = ref(0);
const isPasswordValid = ref(false);
const passwordMessage = ref('');
const unblockModalOpen = ref(false);
const progressColor = ref(NebraskaColors.statusInfo);

const enableButton = computed(
  () => isPasswordValid.value && password.value === passwordConfirm.value
);

const passwordStrengthColor = computed(() => {
  return passwordScore.value <= 0.1
    ? NebraskaColors.statusNegative
    : progressColor.value;
});

const debounceVerifyPassword = debounce(async () => {
  const response = await verifyPassword(password.value);
  if (response) {
    passwordScore.value = response.passwordScore;
    passwordMessage.value = response.passwordMessage;
    isPasswordValid.value = response.isPasswordValid;
    progressColor.value = response.progressColor;
  }
}, 300);

const onInputChange = () => {
  isPasswordValid.value = false;
  debounceVerifyPassword();
};

onMounted(async () => {
  try {
    await checkResetToken({ hash: setupToken.value });
    isLoading.value = false;
  } catch (error) {
    if (error instanceof Error && error.message === 'error_account_blocked') {
      unblockModalOpen.value = true;
    } else {
      notify({
        group: 'wrn-notifications',
        text: 'OPS! ESTA CHAVE DE ACESSO TEMPORÁRIA É INVÁLIDA',
        type: NotificationTypes.error
      });
      setTimeout(() => {
        router.push({ name: 'v3' });
      }, 2000);
    }
  }
});

const commitSetup = async () => {
  comittingSetup.value = true;
  try {
    const response = await commitResetToken({
      hash: setupToken.value,
      password: password.value
    });
    notify({
      group: 'wrn-notifications',
      text: 'Senha alterada com sucesso.',
      type: NotificationTypes.success
    });

    const theme = await getThemeByParam('email', response.user.email);
    const { domain, platform } = getDomainAndPlatform(theme);

    window.location.href = `/onboarding/${domain}/${platform}/login`;
  } catch {
    comittingSetup.value = false;
    notify({
      group: 'wrn-notifications',
      text: 'Não foi possivel registrar a nova senha.',
      type: NotificationTypes.error
    });
  }
};
const unblockAccount = async () => {
  try {
    await unblockHash({ hash: setupToken.value, type: 'reset-password' });
    notify({
      group: 'wrn-notifications',
      text: getString('dialog_unblock_success'),
      type: NotificationTypes.success
    });
  } catch {
    comittingSetup.value = false;
    notify({
      group: 'wrn-notifications',
      text: 'Não foi possivel desbloquear.',
      type: NotificationTypes.error
    });
  }
};
const closeUnblockModal = () => {
  router.push({ name: 'v3' });
};
</script>

<style lang="less" scoped>
@navbar-height: 64px;
@button-container-height: 97px;

.background {
  height: 100vh;
  min-height: 704px;
  background-color: @background-secondary;

  .p5 {
    font-weight: 700;
  }

  .nav {
    width: 100%;
    justify-self: flex-start;
  }
  .custom-nav-bar {
    background-color: transparent !important;
  }
  .content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: @text-negative;
    height: calc(100% - @navbar-height - @button-container-height);
    min-height: 640px;
    .title {
      margin-bottom: @size-spacing-x300;
    }
    .subtitle {
      margin-bottom: @size-spacing-x500;
      text-align: center;
      max-width: 382px;
    }
    .form {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      max-width: 382px;
      margin-top: @size-spacing-x800;
      label {
        color: @text-primary;
        align-self: flex-start;
      }
      .input {
        border-radius: 4px;
        overflow: hidden;
      }
      .mb {
        width: 100%;
      }
      .wrn-c-12 {
        margin-bottom: @size-spacing-x300;
        &:last-of-type {
          margin-bottom: @size-spacing-x500;
        }
      }
    }
  }
}
@media only screen and (max-width: 480px) {
  .form {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    max-width: calc(320px - @size-spacing-x500);
    padding: 0 @size-spacing-x500;
  }
}
</style>
